import { type MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { IPaymentMethodProveedor } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';
import { useFetchPaymentMethodsProveedorParams, useUpdatePaymentMethodProveedor } from '@/shared/actions/erp/payment-methods-proveedor.actions';

///import { useFetchPaymentMethodsProveedor, useUpdatePaymentMethodProveedor } from '@/shared/actions/erp/payment-methods-proveedor.actions';

export type PaymentMethodProveedorPageProps = {};

export const returnUrlPaymentMethodProveedorPage =
  ROUTER_PATHS.administration.metodoPagoProveedorNav;

const PaymentMethodProveedorPage: React.FC<PaymentMethodProveedorPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_formapagoproveedor);

  const navigate = useNavigate();

  ///* global state
  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: PaymentMethodProveedorRes,
    isLoading,
    isRefetching,
  } = useFetchPaymentMethodsProveedorParams({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  console.log({ PaymentMethodProveedorRes })
  //console.log({useFetchPaymentMethodsProveedor})
  ///* mutations
  const updSate = useUpdatePaymentMethodProveedor({
    navigate,
    returnUrl: returnUrlPaymentMethodProveedorPage,
    enableRedirect: false,
  });

  ///* columns
  const columns: any = useMemo<MRT_ColumnDef<IPaymentMethodProveedor>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'alias',
        header: 'Nombre compuesto',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'alias'),
      },
      /* {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */
      {
        accessorKey: 'tipo_contrato',
        header: 'Tipo de Contrato',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_contrato'),
      },
      ///* fk
      {
        accessorKey: 'entidad_financiera',
        header: 'Entidad Financiera',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'entidad_financiera'),
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={speedState => {
                if (!hasPermission(PermissionsEnum.api_change_formapago))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_formapago_proveedor!,
                      data: { estado: !!speedState } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (original: IPaymentMethodProveedor) => {
    navigate(`${returnUrlPaymentMethodProveedorPage}/editar/${original?.id_formapago_proveedor}`);
  };

  return (
    <SingleTableBoxScene
      title="Métodos de Pago Proveedor"
      urlRedirect={`${returnUrlPaymentMethodProveedorPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_formapagoproveedor)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
        cleanFilter={cleanFilter}
      />
      <GeneralTable<IPaymentMethodProveedor>

        columns={columns}
        data={PaymentMethodProveedorRes?.data?.data || []}

        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={PaymentMethodProveedorRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_formapagoproveedor,
          PermissionsEnum.api_view_entidadfinanciera,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_formapagoproveedor,
          PermissionsEnum.api_view_entidadfinanciera,
        ])}
        onEdit={onEdit}
        canDelete={false}
      // onDelete={onDelete}
      />

    </SingleTableBoxScene>
  );
};

export default PaymentMethodProveedorPage;
